import React, { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { Oval } from "react-loader-spinner";
import { AuthGuard } from "../App";
import { EmailGuard } from "../App";
import { StandardURL } from "../App";

const InputField = ({ label, value, onChange, type = "text", ...props }) => (
  <div className="mb-4">
    <label className="block text-gray-700 mb-2">{label}</label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      className="w-full px-3 py-2 border rounded"
      {...props}
    />
  </div>
);

const EmailSender = () => {
  const [emailCategory, setEmailCategory] = useState("tenants");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [imageFiles, setImageFiles] = useState([]);
  const token = localStorage.getItem("token");

  const validateFields = () => {
    if (!subject || !message) {
      toast.error("Subject and message are required");
      return false;
    }

    if (emailCategory === "tenantsByProperty" && !propertyId) {
      toast.error("Property ID is required");
      return false;
    }

    if (emailCategory === "specific" && !email) {
      toast.error("Email address is required");
      return false;
    }

    return true;
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    const uploadedImageUrls = [];

    for (const file of files) {
      const formData = new FormData();
      formData.append("image", file);

      try {
        const response = await axios.post(
          `${StandardURL}/iNyumba/images/images/upload`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );
        uploadedImageUrls.push(response.data.response.coverUri); // Collect uploaded image URLs
      } catch (error) {
        console.error("Error uploading photo:", error);
        toast.error("Failed to upload image.");
      }
    }

    setImageFiles(uploadedImageUrls); // Store image URLs
    const imageTags = uploadedImageUrls
      .map((url) => `<img src="${url}" alt="Image" style="max-width: 100%;" />`)
      .join(""); // Create image tags for the message
    setMessage((prevMessage) => prevMessage + imageTags); // Append image tags to message
  };

  const clearImages = () => {
    setImageFiles([]); // Clear image files
    toast.info("Attached images cleared.");
  };

  const handleEmailSend = async () => {
    if (!validateFields()) return;

    setLoading(true);

    let endpoint = "";
    let data = {
      subject,
      message:
        message +
        imageFiles
          .map(
            (url) => `<img src="${url}" alt="Image" style="max-width: 100%;" />`
          )
          .join(""),
    }; // Append image tags

    switch (emailCategory) {
      case "tenants":
        endpoint = `${StandardURL}/iNyumba/admin/email/tenants`;
        break;
      case "landlords":
        endpoint =
          `${StandardURL}/iNyumba/admin/email/landlords`;
        break;
      case "all":
        endpoint = `${StandardURL}/iNyumba/admin/email/all`;
        break;
      case "tenantsByProperty":
        endpoint = `${StandardURL}/iNyumba/admin/email/tenants/${propertyId}`;
        break;
      case "specific":
        data = { ...data, email };
        endpoint = `${StandardURL}/iNyumba/admin/email/specific`;
        break;
      default:
        break;
    }

    try {
      await axios.post(endpoint, data, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      toast.success("Email sent successfully!");
      // Clear all states after sending
      setSubject("");
      setMessage("");
      setPropertyId("");
      setEmail("");
      setImageFiles([]);
    } catch (error) {
      const errorMessage =
        error.response?.data?.response || "An error occurred.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <EmailGuard>
      <AuthGuard>
        <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-2xl rounded-2xl bg-white p-8 shadow-xl">
            <h1 className="mb-8 text-3xl font-bold tracking-tight text-gray-900">
              Send Emails
            </h1>

            {/* Email Category Selector */}
            <div className="mb-4">
              <label className="mb-2 block text-sm font-medium text-gray-700">Email Category</label>
              <select
                value={emailCategory}
                onChange={(e) => setEmailCategory(e.target.value)}
                className="w-full cursor-pointer rounded-lg border border-gray-300 px-4 py-3 text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="tenants">All Tenants</option>
                <option value="landlords">All Landlords</option>
                <option value="all">Everyone</option>
                <option value="tenantsByProperty">Tenants by Property ID</option>
                <option value="specific">Specific User by Email</option>
              </select>
            </div>

            <InputField
              label="Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />

            <div className="mb-4">
              <label className="mb-2 block text-sm font-medium text-gray-700">Message</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="w-full h-40 resize-none rounded-lg border border-gray-300 px-4 py-3 text-gray-700 placeholder-gray-400 transition-colors duration-200 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            {emailCategory === "tenantsByProperty" && (
              <InputField
                label="Property ID"
                value={propertyId}
                onChange={(e) => setPropertyId(e.target.value)}
              />
            )}

            {emailCategory === "specific" && (
              <InputField
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            )}

            <div className="mb-6">
              <label className="mb-2 block text-sm font-medium text-gray-700">Attach Images</label>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                className="w-full file:rounded-lg file:border-0 file:bg-blue-50 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-blue-700 file:transition-colors file:duration-200 hover:file:bg-blue-100 focus:outline-none"
              />
              {imageFiles.length > 0 && (
                <div className="mt-4 flex items-center justify-between rounded-md bg-gray-100 p-3 text-gray-600">
                  <p className="text-sm">{imageFiles.length} image(s) attached</p>
                  <button
                    onClick={clearImages}
                    className="transform rounded-full bg-red-500 px-4 py-1 text-xs font-semibold text-white shadow-sm transition-all duration-200 hover:scale-105 hover:bg-red-600"
                  >
                    Clear Images
                  </button>
                </div>
              )}
            </div>

            <button
              onClick={handleEmailSend}
              disabled={loading}
              className={`flex w-full items-center justify-center rounded-full bg-blue-600 px-6 py-3 font-semibold text-white shadow-md transition-all duration-200 ${
                loading ? "cursor-not-allowed opacity-70" : "hover:scale-105 hover:bg-blue-700"
              }`}
            >
              {loading ? (
                <Oval
                  height={20}
                  width={20}
                  color="#fff"
                  secondaryColor="#EBF4FF"
                  ariaLabel="loading"
                  strokeWidth={4}
                />
              ) : (
                "Send Email"
              )}
            </button>
          </div>
        </div>
      </AuthGuard>
    </EmailGuard>
  );
};

export default EmailSender;
